import { gradeOfGradePeriod } from "./dibels";

/**
 * Why ORF did not run: DIBELS 8 ORF begins at grade 1, and the ORF norms
 * cover levels 1.1 to 6.3 only, so neither a kindergartner nor a reader
 * whose aWRF placed them at a K level (0.x) has a passage or a percentile.
 * At the beginning of grade 1, DIBELS 8 also ends the period's benchmark
 * assessment, ORF included, when WRF is discontinued.
 */
export const ORF_SKIPPED_REASONS = [
  "kindergarten",
  "instructional_level_below_grade_1",
  "wrf_discontinued_beginning_of_grade_1",
] as const;

export type OrfSkippedReason = (typeof ORF_SKIPPED_REASONS)[number];

const BEGINNING_OF_GRADE_1 = "1.1";

export interface AwrfTimedSegmentFacts {
  segmentStart: number | bigint;
  gradePeriod: string;
  discontinued?: boolean;
}

/**
 * DIBELS 8's Discontinue Benchmark Assessments Rule: no correct word in the
 * first five of the grade-level WRF list at the beginning of grade 1. The
 * grade-level list is the aWRF segment at index 0; a first line missed only
 * on an easier list after a downgrade is not the rule's WRF.
 */
export function wrfEndsBeginningOfGrade1Benchmark(
  timedSegments: readonly AwrfTimedSegmentFacts[],
): boolean {
  const gradeLevelList = timedSegments.find(
    (segment) => Number(segment.segmentStart) === 0,
  );
  return (
    gradeLevelList?.discontinued === true &&
    gradeLevelList.gradePeriod === BEGINNING_OF_GRADE_1
  );
}

/** The reason ORF is skipped for this reader, or null when it runs. */
export function orfSkippedReasonOf({
  trueGrade,
  instructionalGradePeriod,
  wrfEndedBenchmark = false,
}: {
  trueGrade: number | undefined;
  instructionalGradePeriod: string | undefined;
  wrfEndedBenchmark?: boolean;
}): OrfSkippedReason | null {
  if (trueGrade === 0) return "kindergarten";
  if (trueGrade === 1 && wrfEndedBenchmark) {
    return "wrf_discontinued_beginning_of_grade_1";
  }
  if (
    instructionalGradePeriod !== undefined &&
    gradeOfGradePeriod(instructionalGradePeriod) === 0
  ) {
    return "instructional_level_below_grade_1";
  }
  return null;
}
